import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { subDays } from "date-fns";
import type { RootState } from "../store";
import { startOfMonday, toISODate } from "../../utils/dateUtils.ts";

export interface CalendarState {
	sessions: Session[] | [];
	lens: View;
	view: string;
	currentDate: string;
	startDate: string;
	endDate: string;
	selectedPatient: Patient | null;
	selectedEntityType: EntityType | null;
	createDate: string | null;
	/** Monday-anchored first week for the 4-week view, YYYY-MM-DD */
	anchorDate: string;
	weekCycleLabel: "A" | "B" | "C" | "D" | null;
}

const initialState: CalendarState = {
	sessions: [],
	lens: "main",
	view: "dayGridMonth" as string,
	currentDate: new Date().toISOString(),
	startDate: subDays(new Date(), 89).toISOString(),
	endDate: new Date().toISOString(),
	selectedPatient: null,
	selectedEntityType: null,
	createDate: null,
	anchorDate: toISODate(startOfMonday(new Date())),
	weekCycleLabel: null,
};

export const calendarSlice = createSlice({
	name: "calendar",
	initialState,
	reducers: {
		setSessions: (state, action: PayloadAction<Session[] | []>) => {
			state.sessions = action.payload;
		},

		setCalendarLens(state, action: PayloadAction<View>) {
			state.lens = action.payload;
		},

		setCalendarView(state, action: PayloadAction<string>) {
			state.view = action.payload;
		},

		setCalendarDate(state, action: PayloadAction<string>) {
			state.currentDate = action.payload;
		},

		setCreateDate(state, action: PayloadAction<string | null>) {
			state.createDate = action.payload;
		},

		setAnchorDate(state, action: PayloadAction<string>) {
			console.debug("[CALENDAR] setAnchorDate", {
				incoming: action.payload,
			});
			state.anchorDate = action.payload;
		},

		setAnchorDateFromDate(state, action: PayloadAction<string>) {
			const monday = startOfMonday(new Date(action.payload));
			const iso = toISODate(monday);
			console.debug("[CALENDAR] setAnchorDateFromDate", {
				src: action.payload,
				monday: iso,
			});
			state.anchorDate = iso;
		},

		setWeekCycleLabel: (state, action: PayloadAction<WeekCycleLabel>) => {
			state.weekCycleLabel = action.payload;
		},

		updateSessions: (state, action: PayloadAction<Session[] | []>) => {
			state.sessions = [...state.sessions, ...action.payload];
		},

		updateSession: (
			state,
			action: PayloadAction<{
				sessionId: string;
				updates: Partial<Session>;
			}>,
		) => {
			const { sessionId, updates } = action.payload;
			const session = state.sessions.find(
				(session) => session.id === parseInt(sessionId),
			);

			if (session) {
				Object.assign(session, updates);

				if (updates.events) {
					updates.events.forEach((updatedEvent) => {
						state.sessions = state.sessions.map((event) =>
							event.id === updatedEvent.id
								? { ...event, ...updatedEvent }
								: event,
						);
					});
				}
			}
		},

		updateEvent: (
			state,
			action: PayloadAction<{
				sessionId: string;
				eventId: number;
				updates: Partial<Event>;
			}>,
		) => {
			const { sessionId, eventId, updates } = action.payload;
			const session = state.sessions.find(
				(session) => session.id === parseInt(sessionId),
			);

			if (session) {
				const event = session.events.find((e) => e.id === eventId);
				if (event) {
					Object.assign(event, updates);
				}
			}
		},

		removeSession: (state, action: PayloadAction<number>) => {
			state.sessions = state.sessions.filter(
				(event) => event.id !== action.payload,
			);
		},

		setStartDate: (state, action: PayloadAction<string>) => {
			state.startDate = action.payload;
		},

		setEndDate: (state, action: PayloadAction<string>) => {
			state.endDate = action.payload;
		},

		deleteEventsByMonth: (state, action: PayloadAction<string>) => {
			const [year, month] = action.payload.split("-");
			state.sessions = state.sessions.filter((event) => {
				const [eventYear, eventMonth] =
					event.date_of_service.split("-");
				return !(eventYear === year && eventMonth === month);
			});
		},

		setSelectedEntityType(state, action: PayloadAction<EntityType>) {
			state.selectedEntityType = action.payload;
		},

		patchSurgeonSpecialtyOnEvents(
			state,
			action: PayloadAction<{ surgeonId: number; specialty: string[] }>,
		) {
			const { surgeonId, specialty } = action.payload;
			console.debug("[CALENDAR] patchSurgeonSpecialtyOnEvents", {
				surgeonId,
				specialty,
			});

			state.sessions = state.sessions.map((ev) => {
				if (ev?.surgeon?.id === surgeonId) {
					return {
						...ev,
						surgeon: {
							...ev.surgeon,
							specialty: Array.isArray(specialty)
								? specialty
								: [],
						},
					};
				}
				return ev;
			});
		},
	},
});

export const {
	setSessions,
	setCalendarLens,
	setCalendarView,
	setCalendarDate,
	setCreateDate,
	setAnchorDate,
	setAnchorDateFromDate,
	setWeekCycleLabel,
	updateSessions,
	updateSession,
	updateEvent,
	removeSession,
	setStartDate,
	setEndDate,
	deleteEventsByMonth,
	setSelectedEntityType,
	patchSurgeonSpecialtyOnEvents,
} = calendarSlice.actions;

// Selectors.
export const selectStartDate = (state: RootState) => state.calendar.startDate;
export const selectEndDate = (state: RootState) => state.calendar.endDate;
export const selectCalendarViewSel = (state: RootState) => state.calendar.view;
export const selectSelectedEntityType = (state: RootState) =>
	state.calendar.selectedEntityType;
export const selectCreateDate = (state: RootState) => state.calendar.createDate;
export const selectAnchorDate = (state: RootState) => state.calendar.anchorDate;

export default calendarSlice.reducer;

export const lensOptions = [
	{ lens: "main", label: "Main" },
	{ lens: "fundingStatus", label: "Funding Status" },
	{ lens: "confirmation", label: "Confirmation" },
	{ lens: "reconciliation", label: "Reconciliation" },

	// { lens: "uploadStatus", label: "Upload Status" },
	// { lens: "feeEstimate", label: "Fee Estimate" },
	// { lens: "speciality", label: "Speciality" },
	// { lens: "tag", label: "Tag" },
	// { lens: "IFC", label: "IFC" },
];

type View = "main" | "fundingStatus" | "confirmation" | "reconciliation";

// | "uploadStatus"
// | "feeEstimate"
// | "speciality"
// | "tag"
// | "ifc"
// | string;

interface Session {
	id: number;
	title: string;
	start: string;
	end: string;
	date_of_service: string;
	surgeon: {
		id: number;
		acronym?: string;
		name: string;
		[key: string]: any;
	};
	hospital: {
		id: number;
		acronym?: string;
		name: string;
		[key: string]: any;
	};
	events: Event[];
}

interface Event {
	id?: number;
	[key: string]: any;
}

interface Patient {}

export type EntityType =
	| "session"
	| "event"
	| "patient"
	| "createPersonal"
	| "createSession"
	| null;
